import classNames from 'classnames';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceCallback } from 'usehooks-ts';
import { CellItem, TimeGridEntryUuidWeekdayRowUuid } from '../../../stores/TimetableVersion/PinboardStoreTypes';
import {
  AvailabilityResourceType,
  Context,
  DetailedPlacedInfo,
  Time,
} from '../../../stores/TimetableVersion/TimeAvailabilityStore';
import {
  availabilityResourceTypeToWarningType,
  getWarningTypeTranslation,
} from '../../../utils/getWarningTypeTranslation';
import styles from './../TimetableGrid.module.scss';
import { useTimetableStore } from '../TimetableProvider';

/** Zeilen-Kontext des Boards → Ressourcentyp der Zeilen-Entität. */
const contextToResourceType = (context: Context): AvailabilityResourceType => {
  switch (context) {
    case 'teachers':
      return 'teacher';
    case 'rooms':
      return 'room';
    case 'subjects':
      return 'subject';
    case 'divisionGroups':
      return 'divisionGroup';
    default:
      return 'class';
  }
};

type GridCellProps = {
  width: string;
  className?: string | undefined;
  cell: CellItem;
  dayIndex: number;
  isPause?: boolean;
};

export const GridCell: FC<GridCellProps> = observer(({ className, cell, isPause }) => {
  const store = useTimetableStore();
  const availabilityStore = store.getTimeAvailabilityStore();

  const { t } = useTranslation();

  let blocked: boolean = false;
  let maybe: boolean = false;
  let valid: boolean = false;
  let conflictHint: string | undefined;

  // Only subscribe to pickedCard when this cell could actually need it.
  // Without this guard, ALL ~2000 cells re-render on every pick/unpick.
  const needsPickedCard =
    store.context === 'rooms' || cell.row.isFunctionClass || store.rowsToHighlight.has(cell.row.value);

  const pickedCard = needsPickedCard ? store.pickedCard : null;

  if (store.context !== 'rooms' && pickedCard && availabilityStore && store.rowsToHighlight.has(cell.row.value)) {
    valid = store.canBeDropped(pickedCard.cardUuid, pickedCard.duration, cell.hour.value, cell.day.value);
    const timeGridEntries = store.timeGridEntries;

    const timeGridEntry = timeGridEntries?.find((entry) => entry.uuid === cell.hour.value);
    if (timeGridEntry) {
      const start = dayjs(timeGridEntry.start).add(1, 'minute'); // only check for one minute

      const availability = availabilityStore.checkCardAvailability({
        time: start.format('HH:mm') as Time,
        day: cell.day.value,
        card: store.getAvailableCardForCardUuid(pickedCard.cardUuid),
        context: store.context,
      });

      const buildCardLabel = (cardUuid: string): string => {
        const other = store.getCardByUuid(cardUuid);
        if (!other) return '';
        return [other.classes.map((c) => c.shortName).join(', '), other.subject?.shortName].filter(Boolean).join(' ');
      };

      const buildHint = (items: DetailedPlacedInfo[], isBlocked: boolean) =>
        items
          .map((p) => {
            const label = getWarningTypeTranslation(availabilityResourceTypeToWarningType(p.type, isBlocked, false));
            let resourceName = '';
            if (p.type === 'teacher') resourceName = store.teachers.get(p.uuid)?.shortName ?? t('common.unknown');
            else if (p.type === 'class') resourceName = store.classes.get(p.uuid)?.shortName ?? t('common.unknown');
            else if (p.type === 'room') resourceName = store.rooms.get(p.uuid)?.name ?? t('common.unknown');
            else if (p.type === 'divisionGroup') {
              const [, classUuid] = p.uuid.split(':');
              resourceName = store.classes.get(classUuid)?.shortName ?? t('common.unknown');
            }
            const reasonPart = resourceName ? `${label}: ${resourceName}` : label;
            if (isBlocked) return reasonPart;

            let conflictingCardLabel = '';
            if (p.type === 'divisionGroup') {
              const conflictingCardUuid = p.uuid.split(':')[0];
              conflictingCardLabel = buildCardLabel(conflictingCardUuid);
            } else {
              const slotKey = `${cell.hour.value}:${cell.day.value}:${p.uuid}` as TimeGridEntryUuidWeekdayRowUuid;
              const cardUuids = store.cardsBySlotAndResource.get(slotKey) ?? new Set<string>();
              const otherCardLabels = [...cardUuids]
                .filter((u) => u !== pickedCard?.cardUuid)
                .map(buildCardLabel)
                .filter(Boolean);
              conflictingCardLabel = otherCardLabels.join(', ');
            }
            return conflictingCardLabel ? `${conflictingCardLabel} → ${reasonPart}` : reasonPart;
          })
          .join(' | ');

      if (availability.status === 'placed') {
        blocked = true;
        conflictHint = availability.placed ? buildHint(availability.placed, false) : undefined;
      }

      if (availability.status === 'blocked') {
        blocked = true;
        conflictHint = availability.blocked ? buildHint(availability.blocked, true) : undefined;
      }

      if (availability.status === 'maybe') {
        maybe = true;
      }
    }
  }

  if (store.context === 'rooms' && pickedCard) {
    valid = store.canBeDropped(pickedCard.cardUuid, pickedCard.duration, cell.hour.value, cell.day.value);
  }

  // Finding-Zell-Highlight (Hohlstunde, gebrochene Verfügbarkeit, …). Die Map ist pro Zeile
  // gruppiert — Zellen unbeteiligter Zeilen reagieren nicht auf Checkbox-Änderungen. Während
  // eines Drags gewinnt die Drop-Färbung; store.pickedCard wird dank && nur von den wenigen
  // tatsächlich gehighlighteten Zellen gelesen.
  const findingHint = store.getFindingSlotHint(cell.row.value, cell.hour.value, cell.day.value);
  const showFinding = findingHint !== undefined && !blocked && !maybe && !valid && !store.pickedCard;

  // Dauerhafte Nichtverfügbarkeit der Zeilen-Entität. Bewusst OHNE `store.pickedCard`
  // in der Bedingung: das würde jede Zelle auf `pickedCard` abonnieren und beim Picken
  // das komplette Board neu rendern (genau das, was `needsPickedCard` oben vermeidet).
  let unavailable = false;
  let unavailableMaybe = false;
  if (!isPause && availabilityStore) {
    const timeGridEntry = store.timeGridEntries?.find((entry) => entry.uuid === cell.hour.value);
    if (timeGridEntry) {
      const time = dayjs(timeGridEntry.start).add(1, 'minute').format('HH:mm');
      unavailable = availabilityStore.isResourceBlocked(time, cell.day.value, cell.row.value);
      unavailableMaybe = !unavailable && availabilityStore.isResourceMaybe(time, cell.day.value, cell.row.value);
    }
  }

  const unavailableHint =
    unavailable || unavailableMaybe
      ? getWarningTypeTranslation(
          availabilityResourceTypeToWarningType(contextToResourceType(store.context), unavailable, unavailableMaybe),
        )
      : undefined;

  // Mit Karte in der Hand ist jede Zelle der betroffenen Zeilen entweder ablegbar (grün)
  // oder nicht (rot mit ×) — auch wenn der Grund kein Verfügbarkeits-Status ist, sondern
  // z.B. ein Konflikt oder eine nicht passende Dauer. `pickedCard` ist nur in genau diesen
  // Zeilen gesetzt (siehe `needsPickedCard`), alle anderen behalten ihre Dauer-Anzeige.
  const notDroppable = !!pickedCard && !valid;

  // Rangfolge: gesperrt > eingeschränkt > ablegbar. Grün heißt damit "wirklich
  // konfliktfrei" — eine nur eingeschränkt verfügbare Zelle bleibt gelb, auch wenn man
  // dort ablegen könnte. Gesperrt/eingeschränkt sieht beim Picken genauso aus wie die
  // dauerhafte Nichtverfügbarkeit — eine Bildsprache statt rotem/gelbem Rahmen.
  // "Verfügbarkeiten und Konflikte anzeigen" schaltet die komplette Zell-Einfärbung ab —
  // auch die grüne Drop-Rückmeldung, damit beim Ausschalten nichts davon übrig bleibt.
  const showAvailability = store.showWarningsHighlight;
  const showBlocked = showAvailability && (unavailable || blocked || notDroppable);
  const showMaybe = showAvailability && !showBlocked && (unavailableMaybe || maybe);

  const classes = classNames(
    styles['grid-cell'],
    {
      [styles.unavailable]: showBlocked,
      [styles['unavailable-maybe']]: showMaybe,
      [styles.valid]: showAvailability && valid && !showBlocked && !showMaybe,
      [styles.finding]: showFinding,
      [styles.pause]: isPause,
    },
    className,
  );

  const onCellClick = useCallback(
    async (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();
      store.placeCard(cell);
    },
    [cell, store],
  );

  const debounceOnCardHover = useDebounceCallback(() => {
    if (store.pickedCard) {
      store.setHoveredCard(store.pickedCard.cardUuid);
    }
  }, 500);

  const onMouseEnter = () => {
    debounceOnCardHover();
  };
  const onMouseLeave = () => {
    debounceOnCardHover.cancel();
  };

  return (
    <div
      className={classes}
      onClick={onCellClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-conflict-hint={showFinding ? findingHint : (conflictHint ?? unavailableHint)}
    >
      <div className={styles['grid-cell-wrapper']}></div>
    </div>
  );
});
